import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Archive, ArrowLeft, Book, CreditCard, Download, FileText, Gift, Link2, Loader2, Minus, Pencil, Plus, Unlink, XCircle } from 'lucide-react';
import React, { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';
import Content from '../components/layout/Content';
import { paymentMethodConfig, defaultPaymentMethod } from '~/lib/payment-method-config';
import BillingDraftCreator from '~/components/billing-draft-creator/BillingDraftCreator';
import AddPaymentDialog from '~/components/billing-draft-creator/AddPaymentDialog';
import BillingDraftEditor from '~/components/billing-draft-creator/BillingDraftEditor';
import RefundPaymentDialog from '~/components/billing-draft-creator/RefundPaymentDialog';
import BillingDraftLogsModal from '~/components/billing-draft-creator/BillingDraftLogsModal';
import { cancellationFeeTypeLabels } from '~/lib/cancellation-fee-config';

const statusLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    PENDING_PAYMENT: { label: 'Ausstehend', variant: 'outline' },
    PAID: { label: 'Bezahlt', variant: 'default' },
    CANCELLED: { label: 'Storniert', variant: 'destructive' },
};

const billingDraftTypeLabels: Record<string, string> = {
    NORMAL: 'Standard',
    INFLUENCER: 'Influencer',
    MARKETING: 'Marketing',
    ACADEMY: 'Academy',
    KULANZ: 'Kulanz/Beschwerde',
    CANCELLATION_FEE: 'Ausfallentschädigung',
};

const invoiceTypeLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    GOA_INVOICE: { label: 'GOÄ', variant: 'default' },
    GOA_REFUND_INVOICE: { label: 'GOÄ Gutschrift', variant: 'destructive' },
    BUNDLE_INVOICE: { label: 'Bundle', variant: 'secondary' },
    BUNDLE_REFUND_INVOICE: { label: 'Bundle Gutschrift', variant: 'destructive' },
    GIFT_CARD_INVOICE: { label: 'Gutscheine', variant: 'secondary' },
    GIFT_CARD_REFUND_INVOICE: { label: 'Gutschein Gutschrift', variant: 'destructive' },
    GENERAL_INVOICE: { label: 'Allgemein', variant: 'outline' },
    GENERAL_REFUND_INVOICE: { label: 'Allgemein Gutschrift', variant: 'destructive' },
    AUSFALL_INVOICE: { label: 'Ausfallentschädigung', variant: 'secondary' },
    AUSFALL_REFUND_INVOICE: { label: 'Ausfallentschädigung Gutschrift', variant: 'destructive' },
};

const paymentStatusLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    PENDING: { label: 'Ausstehend', variant: 'outline' },
    COMPLETED: { label: 'Abgeschlossen', variant: 'default' },
    FAILED: { label: 'Fehlgeschlagen', variant: 'destructive' },
    REFUNDED: { label: 'Erstattet', variant: 'secondary' },
};

const lineItemStatusLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    PENDING_PAYMENT: { label: 'Ausstehend', variant: 'outline' },
    PAID: { label: 'Bezahlt', variant: 'default' },
    CANCELLED: { label: 'Storniert', variant: 'destructive' },
};

type BillingDraftNavigationState = {
    /** Path to return to when navigating back (e.g. the customer page the draft was opened from) */
    from?: string;
    /** Tab that should be active on the page navigated back to */
    tab?: string;
};

type LineItemDiscountLike = {
    id: string;
    note?: string | null;
    discountCodeId?: string | null;
    discountCode?: { id: string; name: string } | null;
};

/**
 * Admin control to link an existing manual discount on a billing draft to an existing
 * CMS discount code (or remove that link). Only affects utilization tracking - the
 * manually entered discount amount is never changed.
 */
const ManualDiscountLinkControl = ({
    discount,
    billingDraftId,
}: {
    discount: LineItemDiscountLike;
    billingDraftId: string;
}) => {
    const trpcUtils = trpc.useUtils();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    // Only fetch codes once the picker is opened
    const { data: allCodesData, isLoading } = trpc.discountCodes.getAll.useQuery(undefined, { enabled: open });

    const linkMutation = trpc.billingDrafts.linkManualDiscountToCode.useMutation({
        onSuccess: () => {
            toast.success('Rabattcode-Verknüpfung aktualisiert');
            trpcUtils.billingDrafts.getOne.invalidate({ id: billingDraftId });
        },
        onError: (e) => toast.error(`Fehler: ${e.message}`),
    });

    const codes = (allCodesData?.data ?? []).filter((c) => {
        if (!search) { return true; }
        const s = search.toLowerCase();
        return c.name.toLowerCase().includes(s) || c.description.toLowerCase().includes(s);
    });

    if (discount.discountCodeId) {
        return (
            <Button
                variant='ghost'
                size='sm'
                className='h-5 px-1 text-[10px] text-muted-foreground hover:text-destructive'
                disabled={linkMutation.isPending}
                onClick={() => linkMutation.mutate({ discountId: discount.id, discountCodeId: null })}
            >
                {linkMutation.isPending
                    ? <Loader2 className='h-3 w-3 animate-spin mr-0.5' />
                    : <Unlink className='h-3 w-3 mr-0.5' />}
                Verknüpfung entfernen
            </Button>
        );
    }

    return (
        <Popover open={open} onOpenChange={setOpen} modal>
            <PopoverTrigger asChild>
                <Button variant='ghost' size='sm' className='h-5 px-1 text-[10px] text-muted-foreground'>
                    <Link2 className='h-3 w-3 mr-0.5' />
                    Mit Rabattcode verknüpfen
                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-72 p-0' align='start'>
                <Command shouldFilter={false}>
                    <CommandInput placeholder='Rabattcode suchen...' value={search} onValueChange={setSearch} />
                    <CommandList>
                        {isLoading && <div className='p-2 text-xs text-muted-foreground'>Lädt...</div>}
                        {!isLoading && <CommandEmpty>Keine Rabattcodes gefunden</CommandEmpty>}
                        <CommandGroup>
                            {codes.map((c) => (
                                <CommandItem
                                    key={c.id}
                                    value={c.id}
                                    onSelect={() => {
                                        linkMutation.mutate({ discountId: discount.id, discountCodeId: c.id });
                                        setOpen(false);
                                        setSearch('');
                                    }}
                                >
                                    <div className='flex flex-col'>
                                        <span>{c.name}</span>
                                        {c.description && (
                                            <span className='text-xs text-muted-foreground'>{c.description}</span>
                                        )}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

const BillingDraft = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const navState = location.state as BillingDraftNavigationState | null;
    const handleBack = () => {
        if (navState?.from) {
            navigate(navState.from, { state: navState.tab ? { tab: navState.tab } : undefined });
        } else {
            navigate('/billing-drafts');
        }
    };
    const trpcUtils = trpc.useUtils();
    const currentUser = useUserStore((state) => state.user);
    const isAdmin = currentUser?.role === UserRole.ADMIN;
    const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
    const [showRefundPaymentDialog, setShowRefundPaymentDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [cancelLineItemTarget, setCancelLineItemTarget] = useState<any | null>(null);
    const [reverseBundleVoucher, setReverseBundleVoucher] = useState(true);
    const [reverseGiftCard, setReverseGiftCard] = useState(true);
    const [isLastActiveLineItem, setIsLastActiveLineItem] = useState(false);
    // Bundle cancellation state
    const [bundleCancelCheckTarget, setBundleCancelCheckTarget] = useState<{ lineItemId: string } | null>(null);
    const [bundleCancelWarningInfo, setBundleCancelWarningInfo] = useState<{
        vouchers: Array<{ treatmentVariantName: string; unitQuantity: number; usedUnits: number; remainingUnits: number }>;
    } | null>(null);
    const [bundleCancelConfirmInfo, setBundleCancelConfirmInfo] = useState<{
        lineItemId: string;
        vouchers: Array<{ treatmentVariantName: string; unitQuantity: number; remainingUnits: number }>;
    } | null>(null);

    const [showGoaFactorWarning, setShowGoaFactorWarning] = useState(false);

    // Gift card cancellation state
    const [giftCardCancelCheckTarget, setGiftCardCancelCheckTarget] = useState<{ lineItemId: string } | null>(null);
    const [giftCardCancelWarningInfo, setGiftCardCancelWarningInfo] = useState<{
        giftCards: Array<{ giftCardNumber: string; initialValueEur: number; usedAmount: number; remainingBalance: number }>;
    } | null>(null);
    const [giftCardCancelConfirmInfo, setGiftCardCancelConfirmInfo] = useState<{
        lineItemId: string;
        giftCards: Array<{ giftCardNumber: string; initialValueEur: number; remainingBalance: number }>;
    } | null>(null);

    const { data: billingDraft, isLoading, error } = trpc.billingDrafts.getOne.useQuery(
        { id: id! },
        { enabled: !!id && id !== 'new', refetchInterval: 5_000 }
    );

    // Fetch payments for this billing draft
    const { data: payments } = trpc.payments.getByBillingDraft.useQuery(
        { billingDraftId: id! },
        { enabled: !!id && id !== 'new', refetchInterval: 5_000 }
    );

    // Check if the customer has any available gift cards (remaining balance > 0)
    const { data: customerGiftCards } = trpc.giftCards.getByCustomer.useQuery(
        { customerId: billingDraft?.customerId! },
        { enabled: !!billingDraft?.customerId }
    );
    const hasAvailableGiftCard = (customerGiftCards?.data ?? []).some((gc) => gc.remainingBalance > 0);

    // Check if there are Klarna authorizations available
    const { data: klarnaAuthorizations } = trpc.payments.getKlarnaAuthorizationsForBillingDraft.useQuery(
        { billingDraftId: id! },
        { enabled: !!id && id !== 'new', refetchInterval: 5_000 }
    );
    const hasKlarnaAuthorization = (klarnaAuthorizations ?? []).length > 0;
    const { data: expiredKlarnaAuthorizations } = trpc.payments.getExpiredKlarnaAuthorizationsForBillingDraft.useQuery(
        { billingDraftId: id! },
        { enabled: !!id && id !== 'new' }
    );
    const latestExpiredKlarnaAuth = expiredKlarnaAuthorizations?.[0];

    // Fetch invoices generated from this billing draft
    const { data: invoices, isLoading: invoicesLoading } = trpc.invoices.getByBillingDraft.useQuery(
        { billingDraftId: id! },
        { enabled: !!id && id !== 'new', refetchInterval: 5_000 }
    );

    const cancelMutation = trpc.billingDrafts.cancel.useMutation({
        onSuccess: (data) => {
            const count = data.cancelledInvoices.length;
            if (count > 0) {
                toast.success(`Auftrag storniert. ${count} Storno-Rechnung(en) erstellt.`);
            } else {
                toast.success('Auftrag wurde storniert');
            }
            trpcUtils.billingDrafts.getOne.invalidate({ id: id! });
            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id! });
            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId: id! });
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const downloadInvoiceMutation = trpc.invoices.getDownloadUrl.useMutation({
        onSuccess: (data) => {
            window.open(data.downloadUrl, '_blank');
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const cancelLineItemMutation = trpc.billingDrafts.cancelLineItem.useMutation({
        onSuccess: (data) => {
            const count = data.cancelledInvoices.length;
            if (count === 0) {
                toast.success('Position storniert.');
            } else if (count === 1) {
                toast.success(`Position storniert. Storno-Rechnung ${data.cancelledInvoices[0].invoiceNumber} erstellt.`);
            } else {
                toast.success(`Position storniert. ${count} Storno-Rechnungen erstellt.`);
            }
            trpcUtils.billingDrafts.getOne.invalidate({ id: id! });
            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id! });
            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId: id! });
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const cancelBundleWithArchiveMutation = trpc.billingDrafts.cancelBundleWithArchive.useMutation({
        onSuccess: (data) => {
            const count = data.cancelledInvoices.length;
            if (count === 1) {
                toast.success(`Bundle storniert und archiviert. Storno-Rechnung ${data.cancelledInvoices[0].invoiceNumber} erstellt.`);
            } else {
                toast.success(`Bundle storniert und archiviert. ${count} Storno-Rechnungen erstellt.`);
            }
            trpcUtils.billingDrafts.getOne.invalidate({ id: id! });
            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id! });
            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId: id! });
            setBundleCancelConfirmInfo(null);
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const cancelGiftCardWithArchiveMutation = trpc.billingDrafts.cancelGiftCardWithArchive.useMutation({
        onSuccess: (data) => {
            const count = data.cancelledInvoices.length;
            if (count === 1) {
                toast.success(`Gutscheinkarte storniert und archiviert. Storno-Rechnung ${data.cancelledInvoices[0].invoiceNumber} erstellt.`);
            } else {
                toast.success(`Gutscheinkarte storniert und archiviert. ${count} Storno-Rechnungen erstellt.`);
            }
            trpcUtils.billingDrafts.getOne.invalidate({ id: id! });
            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id! });
            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId: id! });
            setGiftCardCancelConfirmInfo(null);
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const archiveMutation = trpc.billingDrafts.archive.useMutation({
        onSuccess: () => {
            toast.success('Auftrag wurde archiviert');
            navigate('/billing-drafts');
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const generateInvoicesMutation = trpc.billingDrafts.generateInvoices.useMutation({
        onSuccess: (data) => {
            toast.success(`${data.invoicesCount} Rechnung(en) erfolgreich erstellt`);
            trpcUtils.billingDrafts.getOne.invalidate({ id: id! });
            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id! });
            if (data.goaInvoices.hasFactorBelowOne) {
                setShowGoaFactorWarning(true);
            }
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    // Fetch bundle cancellation info when a check target is set
    const { data: bundleCancellationInfo, isFetching: isFetchingBundleCancelInfo } = trpc.billingDrafts.getBundleCancellationInfo.useQuery(
        bundleCancelCheckTarget!,
        {
            enabled: !!bundleCancelCheckTarget,
            // Don't cache this — always fetch fresh info
            gcTime: 0,
            staleTime: 0,
        }
    );

    // React to the cancellation info result
    React.useEffect(() => {
        if (!bundleCancelCheckTarget || !bundleCancellationInfo || isFetchingBundleCancelInfo) {
            return;
        }

        if (!bundleCancellationInfo.canCancel) {
            setBundleCancelWarningInfo({ vouchers: bundleCancellationInfo.vouchers });
        } else {
            setBundleCancelConfirmInfo({
                lineItemId: bundleCancelCheckTarget.lineItemId,
                vouchers: bundleCancellationInfo.vouchers,
            });
        }

        setBundleCancelCheckTarget(null);
    }, [bundleCancellationInfo, bundleCancelCheckTarget, isFetchingBundleCancelInfo]);

    const handleBundleCancelCheck = (lineItemId: string) => {
        setBundleCancelCheckTarget({ lineItemId });
    };

    // Fetch gift card cancellation info when a check target is set
    const { data: giftCardCancellationInfo, isFetching: isFetchingGiftCardCancelInfo } = trpc.billingDrafts.getGiftCardCancellationInfo.useQuery(
        giftCardCancelCheckTarget!,
        {
            enabled: !!giftCardCancelCheckTarget,
            gcTime: 0,
            staleTime: 0,
        }
    );

    // React to the gift card cancellation info result
    React.useEffect(() => {
        if (!giftCardCancelCheckTarget || !giftCardCancellationInfo || isFetchingGiftCardCancelInfo) {
            return;
        }

        if (!giftCardCancellationInfo.canCancel) {
            setGiftCardCancelWarningInfo({ giftCards: giftCardCancellationInfo.giftCards });
        } else {
            setGiftCardCancelConfirmInfo({
                lineItemId: giftCardCancelCheckTarget.lineItemId,
                giftCards: giftCardCancellationInfo.giftCards,
            });
        }

        setGiftCardCancelCheckTarget(null);
    }, [giftCardCancellationInfo, giftCardCancelCheckTarget, isFetchingGiftCardCancelInfo]);

    const handleGiftCardCancelCheck = (lineItemId: string) => {
        setGiftCardCancelCheckTarget({ lineItemId });
    };

    // Redirect to billing draft creator for new billing drafts
    if (id === 'new') {
        return <BillingDraftCreatorPage />;
    }

    if (isLoading) {
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <Skeleton className='h-8 w-48 mb-6' />
                    <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
                        <div className='lg:col-span-2 space-y-6'>
                            <Skeleton className='h-48' />
                            <Skeleton className='h-64' />
                        </div>
                        <div>
                            <Skeleton className='h-48' />
                        </div>
                    </div>
                </div>
            </Content>
        );
    }

    if (error || !billingDraft) {
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <div className='text-center'>
                        <h1 className='text-2xl font-bold mb-4'>Auftrag nicht gefunden</h1>
                        <Button onClick={handleBack}>
                            <ArrowLeft className='mr-2 h-4 w-4' />
                            Zurück zur Übersicht
                        </Button>
                    </div>
                </div>
            </Content>
        );
    }

    const status = statusLabels[billingDraft.status];
    const formatCurrency = (amount: number | string) => {
        const num = typeof amount === 'string' ? parseFloat(amount) : amount;
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(num);
    };

    return (
        <Content>
            <div className='container mx-auto py-10'>
                {/* Header */}
                <div className='flex items-center justify-between mb-6'>
                    <div className='flex items-center gap-4'>
                        <Button variant='ghost' size='icon' onClick={handleBack}>
                            <ArrowLeft className='h-4 w-4' />
                        </Button>
                        <div>
                            <div className='flex items-center gap-2'>
                                <h1 className='text-2xl font-bold'>{billingDraft.billingDraftNumber}</h1>
                                {(billingDraft as any).influencerType && (
                                    <Badge variant='outline' className='text-xs'>
                                        {{ BEAUTY_INSIDER: 'Beauty Insider', INFLUENCER: 'Influencer Normal', INFLUENCER_FLATRATE: 'Influencer Pauschalversteuerung' }[(billingDraft as any).influencerType] || (billingDraft as any).influencerType}
                                    </Badge>
                                )}
                            </div>
                            <p className='text-muted-foreground'>
                                {billingDraftTypeLabels[billingDraft.type] || billingDraft.type} vom {format(new Date(billingDraft.billingDraftDate), 'dd. MMMM yyyy', { locale: de })}
                            </p>
                        </div>
                    </div>
                    <div className='flex items-center gap-2'>
                        <Badge variant={status.variant} className='text-sm'>
                            {status.label}
                        </Badge>
                    </div>
                </div>

                <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
                    {/* Main Content */}
                    <div className='lg:col-span-2 space-y-6'>
                        {/* Billing Draft Details */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Entwurfsdetails</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className='grid grid-cols-2 gap-6'>
                                    <div>
                                        <h4 className='font-medium mb-2'>Aussteller</h4>
                                        <p className='text-sm text-muted-foreground'>
                                            {billingDraft.companyName}<br />
                                            {billingDraft.companyAddress}<br />
                                            {billingDraft.companyCity}<br />
                                            {billingDraft.companyCountry}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className='font-medium mb-2'>Empfänger</h4>
                                        <p className='text-sm text-muted-foreground'>
                                            {billingDraft.customerName}<br />
                                            Kundennr.: {billingDraft.customerNumber}<br />
                                            {billingDraft.customerStreet}<br />
                                            {billingDraft.customerZipcode} {billingDraft.customerCity}<br />
                                            {billingDraft.customerCountry}
                                        </p>
                                    </div>
                                </div>
                                <Separator className='my-4' />
                                <div className='grid grid-cols-3 gap-4 text-sm'>
                                    <div>
                                        <span className='text-muted-foreground'>Behandlungsdatum:</span>
                                        <p className='font-medium'>{format(new Date(billingDraft.treatmentDate), 'dd.MM.yyyy', { locale: de })}</p>
                                    </div>
                                    <div>
                                        <span className='text-muted-foreground'>Entwurfsdatum:</span>
                                        <p className='font-medium'>{format(new Date(billingDraft.billingDraftDate), 'dd.MM.yyyy', { locale: de })}</p>
                                    </div>
                                    <div>
                                        <span className='text-muted-foreground'>Fällig am:</span>
                                        <p className='font-medium'>{format(new Date(billingDraft.dueDate), 'dd.MM.yyyy', { locale: de })}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Line Items */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Positionen</CardTitle>
                            </CardHeader>
                            <CardContent className='p-0'>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className='w-12'>Pos.</TableHead>
                                            <TableHead>Beschreibung</TableHead>
                                            <TableHead className='text-right'>Netto</TableHead>
                                            <TableHead className='text-right'>MwSt.</TableHead>
                                            <TableHead className='text-right'>Brutto</TableHead>
                                            {billingDraft.status !== 'CANCELLED' && (
                                                <TableHead className='w-12'></TableHead>
                                            )}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {billingDraft.lineItems.map((item) => {
                                            const isCancelled = item.status === 'CANCELLED';
                                            const statusInfo = lineItemStatusLabels[item.status] || { label: item.status, variant: 'outline' as const };

                                            return (
                                                <TableRow key={item.id} className={isCancelled ? 'opacity-60' : ''}>
                                                    <TableCell className={`font-mono text-sm ${isCancelled ? 'line-through' : ''}`}>
                                                        {item.sequence}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className='space-y-1'>
                                                            <div className='flex items-center gap-2'>
                                                                <p className={`font-medium ${isCancelled ? 'line-through' : ''}`}>
                                                                    {item.description}
                                                                </p>
                                                                {item.cancellationFeeType && (
                                                                    <Badge variant='secondary'>
                                                                        {cancellationFeeTypeLabels[item.cancellationFeeType]}
                                                                    </Badge>
                                                                )}
                                                                {isCancelled && (
                                                                    <Badge variant={statusInfo.variant}>
                                                                        {statusInfo.label}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            {(item as any).discounts && (item as any).discounts.length > 0 && !isCancelled && (
                                                                <div className='flex flex-col gap-1'>
                                                                    <div className='flex flex-wrap items-center gap-1'>
                                                                        {(item as any).discounts.map((d: any) => {
                                                                            const isManual = !!d.note || !d.discountCodeId;
                                                                            // Once a manual discount is linked to an existing CMS discount code,
                                                                            // present it as that actual discount rather than a manual one.
                                                                            const isLinkedToCode = !!d.discountCode?.name;
                                                                            const discountLabel = isLinkedToCode
                                                                                ? d.discountCode.name
                                                                                : (isManual ? 'Manueller Rabatt' : 'Rabatt');
                                                                            return (
                                                                                <span key={d.id} className='inline-flex items-center gap-1'>
                                                                                    <Badge variant='outline' className='text-[10px] px-1.5 py-0 text-green-700 border-green-300'>
                                                                                        {discountLabel}: -{formatCurrency(Number(d.discountAmountEur))}
                                                                                    </Badge>
                                                                                    {isAdmin && isManual && (
                                                                                        <ManualDiscountLinkControl discount={d} billingDraftId={billingDraft.id} />
                                                                                    )}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                    {(item as any).discounts.some((d: any) => d.note && !d.discountCode?.name) && (
                                                                        <div className='flex flex-col gap-0.5'>
                                                                            {(item as any).discounts.filter((d: any) => d.note && !d.discountCode?.name).map((d: any) => (
                                                                                <span key={`note-${d.id}`} className='text-[10px] text-muted-foreground italic'>
                                                                                    {d.note}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className={`text-right font-mono ${isCancelled ? 'line-through' : ''}`}>
                                                        {formatCurrency(Number((item as any).totalNetDiscounted))}
                                                    </TableCell>
                                                    <TableCell className={`text-right font-mono ${isCancelled ? 'line-through' : ''}`}>
                                                        {formatCurrency(Number((item as any).vatAmountDiscounted))}
                                                    </TableCell>
                                                    <TableCell className={`text-right font-mono font-medium ${isCancelled ? 'line-through' : ''}`}>
                                                        {Number((item as any).grossDiscountAmount) > 0 && !isCancelled && (
                                                            <div className='text-xs text-muted-foreground line-through font-normal'>
                                                                {formatCurrency(Number(item.totalGross))}
                                                            </div>
                                                        )}
                                                        {formatCurrency(Number((item as any).totalGrossDiscounted))}
                                                    </TableCell>
                                                    {billingDraft.status !== 'CANCELLED' && (
                                                        <TableCell className='text-center'>
                                                            {!isCancelled && (
                                                                <Button
                                                                    variant='ghost'
                                                                    size='icon'
                                                                    className='h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10'
                                                                    disabled={cancelLineItemMutation.isPending || cancelBundleWithArchiveMutation.isPending || cancelGiftCardWithArchiveMutation.isPending || isFetchingBundleCancelInfo || isFetchingGiftCardCancelInfo}
                                                                    onClick={() => {
                                                                        const activeLineItems = billingDraft.lineItems.filter((li: any) => li.status !== 'CANCELLED');
                                                                        const lastActive = activeLineItems.length === 1;
                                                                        setIsLastActiveLineItem(lastActive);

                                                                        const isBundlePurchaseItem = !!item.bundle;
                                                                        const isGiftCardPurchaseItem = !!(item as any).giftCardTemplateId;
                                                                        if (isBundlePurchaseItem) {
                                                                            handleBundleCancelCheck(item.id);
                                                                        } else if (isGiftCardPurchaseItem) {
                                                                            handleGiftCardCancelCheck(item.id);
                                                                        } else {
                                                                            setCancelLineItemTarget(item);
                                                                            setReverseBundleVoucher(true);
                                                                            setReverseGiftCard(lastActive);
                                                                        }
                                                                    }}
                                                                >
                                                                    {(isFetchingBundleCancelInfo && bundleCancelCheckTarget?.lineItemId === item.id) || (isFetchingGiftCardCancelInfo && giftCardCancelCheckTarget?.lineItemId === item.id) ? (
                                                                        <Loader2 className='h-4 w-4 animate-spin' />
                                                                    ) : (
                                                                        <XCircle className='h-4 w-4' />
                                                                    )}
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                                <Separator />
                                <div className='p-4 space-y-2'>
                                    {(() => {
                                        const activeLineItems = billingDraft.lineItems.filter((li: any) => li.status !== 'CANCELLED');
                                        const totalDiscountAmount = activeLineItems.reduce((sum: number, li: any) => {
                                            return sum + ((li.discounts || []) as any[]).reduce((dSum: number, d: any) => dSum + Number(d.discountAmountEur), 0);
                                        }, 0);
                                        const totalGrossOriginal = activeLineItems.reduce((sum: number, li: any) => sum + Number(li.totalGross), 0);
                                        return totalDiscountAmount > 0 ? (
                                            <>
                                                <div className='flex justify-between text-sm text-muted-foreground'>
                                                    <span>Bruttobetrag:</span>
                                                    <span className='font-mono'>{formatCurrency(totalGrossOriginal)}</span>
                                                </div>
                                                <div className='flex justify-between text-sm text-green-600 font-medium'>
                                                    <span>Rabatt gesamt:</span>
                                                    <span className='font-mono'>-{formatCurrency(totalDiscountAmount)}</span>
                                                </div>
                                            </>
                                        ) : null;
                                    })()}
                                    <div className='flex justify-between text-sm'>
                                        <span>Nettobetrag:</span>
                                        <span className='font-mono'>{formatCurrency(Number(billingDraft.totalNetDiscounted))}</span>
                                    </div>
                                    <div className='flex justify-between text-sm'>
                                        <span>MwSt.:</span>
                                        <span className='font-mono'>{formatCurrency(Number(billingDraft.vatAmountDiscounted))}</span>
                                    </div>
                                    <Separator />
                                    <div className='flex justify-between text-lg font-bold'>
                                        <span>Gesamtbetrag:</span>
                                        <span className='font-mono'>{formatCurrency(Number(billingDraft.totalGrossDiscounted))}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        {/* Invoices Section - hidden for MARKETING and BEAUTY_INSIDER influencer billing drafts (no invoices are ever created for them) */}
                        {billingDraft.type !== 'MARKETING' && billingDraft.type !== 'KULANZ' && !(billingDraft.type === 'INFLUENCER' && (billingDraft as any).influencerType === 'BEAUTY_INSIDER') && <Card>
                            <CardHeader>
                                <div className='flex items-center gap-2'>
                                    <CardTitle>Rechnungen</CardTitle>
                                    {invoices && invoices.length > 0 && (
                                        <Badge variant='secondary' className='text-xs'>
                                            {invoices.length}
                                        </Badge>
                                    )}
                                </div>
                                <CardDescription>
                                    {invoices && invoices.length > 0
                                        ? 'Rechnungen wurden erstellt'
                                        : 'Werden automatisch nach der ersten Zahlung oder nach 60 Minuten erstellt'}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {/* Manual invoice generation button - shown when no invoices exist, for NORMAL and INFLUENCER subtypes the cron also generates invoices for (INFLUENCER / INFLUENCER_FLATRATE). */}
                                {!invoicesLoading
                                    && (!invoices || invoices.length === 0)
                                    && billingDraft.type !== 'MARKETING'
                                    && billingDraft.type !== 'KULANZ'
                                    && !(billingDraft.type === 'INFLUENCER' && (billingDraft as any).influencerType !== 'INFLUENCER' && (billingDraft as any).influencerType !== 'INFLUENCER_FLATRATE')
                                    && billingDraft.status !== 'CANCELLED' && (
                                    <div className='mb-4'>
                                        <Button
                                            variant='outline'
                                            className='w-full'
                                            onClick={() => generateInvoicesMutation.mutate({ id: billingDraft.id })}
                                            disabled={generateInvoicesMutation.isPending}
                                        >
                                            {generateInvoicesMutation.isPending ? (
                                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            ) : (
                                                <FileText className='mr-2 h-4 w-4' />
                                            )}
                                            Rechnungen erstellen
                                        </Button>
                                    </div>
                                )}
                                {invoicesLoading ? (
                                    <div className='space-y-2'>
                                        <Skeleton className='h-12' />
                                        <Skeleton className='h-12' />
                                    </div>
                                ) : invoices && invoices.length > 0 ? (
                                    <div className='space-y-3'>
                                        {invoices.map((invoice) => {
                                            const typeInfo = invoiceTypeLabels[invoice.type] || { label: invoice.type, variant: 'outline' as const };
                                            const isRefund = invoice.type.includes('REFUND');
                                            const hasRefund = invoice.refundInvoices && invoice.refundInvoices.length > 0;
                                            const isRefundOf = !!invoice.refundSourceInvoice;

                                            return (
                                                <div key={invoice.id} className='border rounded-lg p-4'>
                                                    <div className='flex items-center justify-between mb-2'>
                                                        <div className='flex items-center gap-2'>
                                                            <Badge variant={typeInfo.variant}>
                                                                {typeInfo.label}
                                                            </Badge>
                                                            <span className='font-mono text-sm font-medium'>
                                                                {invoice.invoiceNumber}
                                                            </span>
                                                        </div>
                                                        <span className='text-sm text-muted-foreground'>
                                                            {format(new Date(invoice.invoiceDate), 'dd.MM.yyyy', { locale: de })}
                                                        </span>
                                                    </div>
                                                    <div className='flex items-center justify-between'>
                                                        <div className='text-sm'>
                                                            <span className='font-mono font-medium'>
                                                                {formatCurrency(invoice.totalGross)}
                                                            </span>
                                                            {isRefundOf && invoice.refundSourceInvoice && (
                                                                <span className='text-muted-foreground ml-2'>
                                                                    (Gutschrift zu {invoice.refundSourceInvoice.invoiceNumber})
                                                                </span>
                                                            )}
                                                            {hasRefund && invoice.refundInvoices.map((refund) => (
                                                                <span key={refund.id} className='text-muted-foreground ml-2'>
                                                                    → Gutschrift: {refund.invoiceNumber}
                                                                </span>
                                                            ))}
                                                        </div>
                                                        <Button
                                                            variant='ghost'
                                                            size='sm'
                                                            onClick={() => downloadInvoiceMutation.mutate({ id: invoice.id })}
                                                            disabled={downloadInvoiceMutation.isPending}
                                                        >
                                                            {downloadInvoiceMutation.isPending ? (
                                                                <Loader2 className='h-4 w-4 animate-spin' />
                                                            ) : (
                                                                <Download className='h-4 w-4' />
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className='flex flex-col items-center gap-2 py-6'>
                                        <FileText className='h-6 w-6 text-muted-foreground' />
                                        <p className='text-sm text-muted-foreground text-center'>
                                            Noch keine Rechnungen erstellt. Rechnungen werden automatisch nach der ersten Zahlung oder nach 60 Minuten generiert.
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>}
                    </div>

                    {/* Sidebar */}
                    <div className='space-y-6'>
                        {/* Actions */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Aktionen</CardTitle>
                            </CardHeader>
                            <CardContent className='space-y-2'>
                                {billingDraft.status !== 'CANCELLED' && (
                                    <>
                                        {billingDraft.type !== 'INFLUENCER' && billingDraft.type !== 'MARKETING' && billingDraft.type !== 'KULANZ' && (() => {
                                            const totalPaidCents = payments?.reduce((sum, p) =>
                                                p.status === 'COMPLETED' ? sum + p.paidAmountCents : sum, 0
                                            ) ?? 0;
                                            const totalPaidEur = totalPaidCents / 100;
                                            const billingDraftTotal = Number(billingDraft.totalGrossDiscounted);
                                            const remaining = billingDraftTotal - totalPaidEur;
                                            return remaining > 0.01;
                                        })() && (
                                            <Button
                                                className='w-full'
                                                onClick={() => setShowAddPaymentDialog(true)}
                                            >
                                                <Plus className='mr-2 h-4 w-4' />
                                                Zahlung hinzufügen
                                            </Button>
                                        )}
                                        <Button
                                            variant='outline'
                                            className='w-full'
                                            disabled={!invoices || invoicesLoading || invoices.length > 0 || billingDraft.type === 'CANCELLATION_FEE'}
                                            title={billingDraft.type === 'CANCELLATION_FEE'
                                                ? 'Ausfallentschädigungen können nicht bearbeitet werden'
                                                : invoices && invoices.length > 0 ? 'Bearbeitung nicht möglich, da bereits Rechnungen erstellt wurden' : undefined}
                                            onClick={() => setShowEditDialog(true)}
                                        >
                                            <Pencil className='mr-2 h-4 w-4' />
                                            Bearbeiten
                                        </Button>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button variant='outline' className='w-full'>
                                                    <XCircle className='mr-2 h-4 w-4' />
                                                    Stornieren
                                                </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Auftrag stornieren?</AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                        Diese Aktion kann nicht rückgängig gemacht werden. Der Auftrag wird als storniert markiert.
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                                    <AlertDialogAction
                                                        onClick={() => cancelMutation.mutate({ id: billingDraft.id })}
                                                    >
                                                        Stornieren
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </>
                                )}

                                {(!invoices || invoices.length === 0) && (
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button variant='ghost' className='w-full text-muted-foreground'>
                                                <Archive className='mr-2 h-4 w-4' />
                                                Archivieren
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Auftrag archivieren?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    Der Auftrag wird archiviert und aus der Übersicht entfernt.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                                <AlertDialogAction
                                                    onClick={() => archiveMutation.mutate({ id: billingDraft.id })}
                                                >
                                                    Archivieren
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                )}
                                <Button
                                    variant='outline'
                                    className='w-full'
                                    onClick={() => setShowLogsModal(true)}
                                >
                                    <Book className='mr-2 h-4 w-4' />
                                    Logs anzeigen
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Payments */}
                        {billingDraft.type !== 'INFLUENCER' && billingDraft.type !== 'MARKETING' && billingDraft.type !== 'KULANZ' && <Card>
                            <CardHeader>
                                <CardTitle>Zahlungen</CardTitle>
                                <CardDescription>
                                    {(() => {
                                        const totalPaidCents = payments?.reduce((sum, p) => 
                                            p.status === 'COMPLETED' ? sum + p.paidAmountCents : sum, 0
                                        ) ?? 0;
                                        const totalPaidEur = totalPaidCents / 100;
                                        const billingDraftTotal = Number(billingDraft.totalGrossDiscounted);
                                        const remaining = billingDraftTotal - totalPaidEur;
                                        
                                        if (remaining > 0.01) {
                                            return <span className='text-red-600 font-medium'>Offen: {formatCurrency(remaining)}</span>;
                                        }
                                        if (remaining < -0.01) {
                                            return <span className='text-red-600 font-medium'>Überzahlung: {formatCurrency(Math.abs(remaining))}</span>;
                                        }
                                        return <span className='text-green-600 font-medium'>Vollständig bezahlt</span>;
                                    })()}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className='space-y-3'>
                                {payments && payments.length > 0 ? (
                                    <div className='space-y-2'>
                                        {payments.map((payment) => {
                                            const method = paymentMethodConfig[payment.paymentMethod ?? ''] ?? defaultPaymentMethod;
                                            const status = paymentStatusLabels[payment.status] ?? paymentStatusLabels.PENDING;
                                            const Icon = method.icon;
                                            const isRefund = payment.type === 'BILLING_DRAFT_REFUND';
                                            
                                            const paymentUrl = `/payment/${payment.id}`;
                                            return (
                                                <a
                                                    key={payment.id}
                                                    href={paymentUrl}
                                                    className='flex items-center justify-between py-2 border-b last:border-0 cursor-pointer hover:bg-muted/50 rounded-sm px-1 -mx-1 no-underline text-inherit'
                                                    onClick={(e) => {
                                                        if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                                                            e.preventDefault();
                                                            navigate(paymentUrl);
                                                        }
                                                    }}
                                                >
                                                    <div className='flex items-center gap-2'>
                                                        <Icon className={`h-4 w-4 ${isRefund ? 'text-destructive' : 'text-muted-foreground'}`} />
                                                        <div>
                                                            <p className={`text-sm font-medium ${isRefund ? 'text-destructive' : ''}`}>
                                                                {formatCurrency(payment.paidAmountCents / 100)}
                                                                {isRefund && <span className='ml-1 text-xs font-normal'>(Rückzahlung)</span>}
                                                            </p>
                                                            <p className='text-xs text-muted-foreground'>
                                                                {method.label} • {format(new Date(payment.createdAt), 'dd.MM.yy HH:mm', { locale: de })}
                                                            </p>
                                                            {payment.paymentMethod === 'GIFT_CARD' && payment.giftCardTransaction?.giftCard?.giftCardNumber && (
                                                                <p className='text-xs text-muted-foreground'>
                                                                    Gutscheinnr.: {payment.giftCardTransaction.giftCard.giftCardNumber}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <Badge variant={status.variant} className='text-xs'>
                                                        {status.label}
                                                    </Badge>
                                                </a>
                                            );
                                        })}
                                        
                                    </div>
                                ) : (
                                    <p className='text-sm text-muted-foreground text-center py-2'>
                                        Keine Zahlungen vorhanden
                                    </p>
                                )}
                                
                                {(() => {
                                    const totalPaidCents = payments?.reduce((sum, p) =>
                                        p.status === 'COMPLETED' ? sum + p.paidAmountCents : sum, 0
                                    ) ?? 0;
                                    const totalPaidEur = totalPaidCents / 100;
                                    const billingDraftTotal = Number(billingDraft.totalGrossDiscounted);
                                    const remaining = billingDraftTotal - totalPaidEur;
                                    return remaining > 0.01 && billingDraft.status !== 'CANCELLED';
                                })() && (
                                    <>
                                        {hasAvailableGiftCard && (
                                            <div className='flex items-center gap-1.5 justify-center text-orange-500 text-sm font-medium'>
                                                <Gift className='h-4 w-4' />
                                                <span>Gutschein verfügbar</span>
                                            </div>
                                        )}
                                        {hasKlarnaAuthorization && (
                                            <div className='flex items-center gap-1.5 justify-center text-blue-600 text-sm font-medium'>
                                                <CreditCard className='h-4 w-4' />
                                                <span>Klarna-Vorautorisierung verfügbar</span>
                                            </div>
                                        )}
                                        {!hasKlarnaAuthorization && latestExpiredKlarnaAuth?.expiresAt && (
                                            <div className='flex items-center gap-1.5 justify-center text-red-600 text-sm font-medium'>
                                                <CreditCard className='h-4 w-4' />
                                                <span>
                                                    Klarna-Vorautorisierung abgelaufen am{' '}
                                                    {format(new Date(latestExpiredKlarnaAuth.expiresAt), 'dd.MM.yyyy', { locale: de })}
                                                </span>
                                            </div>
                                        )}
                                        <Button
                                            variant='outline'
                                            className='w-full'
                                            onClick={() => setShowAddPaymentDialog(true)}
                                        >
                                            <Plus className='mr-2 h-4 w-4' />
                                            Zahlung hinzufügen
                                        </Button>
                                    </>
                                )}
                                {(() => {
                                    const totalPaidCents = payments?.reduce((sum, p) =>
                                        p.status === 'COMPLETED' ? sum + p.paidAmountCents : sum, 0
                                    ) ?? 0;
                                    const totalPaidEur = totalPaidCents / 100;
                                    const billingDraftTotal = Number(billingDraft.totalGrossDiscounted);
                                    const overpayment = totalPaidEur - billingDraftTotal;
                                    return overpayment > 0.01;
                                })() && (
                                    <Button
                                        variant='outline'
                                        className='w-full text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive'
                                        onClick={() => setShowRefundPaymentDialog(true)}
                                    >
                                        <Minus className='mr-2 h-4 w-4' />
                                        Rückzahlung hinzufügen
                                    </Button>
                                )}
                            </CardContent>
                        </Card>}

                        {/* Related Info */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Verknüpfungen</CardTitle>
                            </CardHeader>
                            <CardContent className='space-y-4'>
                                <div>
                                    <span className='text-sm text-muted-foreground'>Kunde</span>
                                    <Button
                                        variant='link'
                                        className='p-0 h-auto block'
                                        onClick={() => navigate(`/customer/${billingDraft.customerId}`)}
                                    >
                                        {billingDraft.customer.firstName} {billingDraft.customer.lastName}
                                    </Button>
                                </div>
                                <div>
                                    <span className='text-sm text-muted-foreground'>Filiale</span>
                                    <p className='font-medium'>{billingDraft.store.name}</p>
                                </div>
                                {billingDraft.appointment && (() => {
                                    const steps = billingDraft.appointment.steps;
                                    const from = steps.length ? new Date(Math.min(...steps.map(s => new Date(s.dateFrom).getTime()))) : null;
                                    const until = steps.length ? new Date(Math.max(...steps.map(s => new Date(s.dateUntil).getTime()))) : null;
                                    const practitioners = [...new Map(steps.flatMap(s => s.practitioners.map(p => [p.practitioner.id, p.practitioner] as const))).values()];
                                    return (
                                        <div>
                                            <span className='text-sm text-muted-foreground'>Termin</span>
                                            <p className='font-medium'>{billingDraft.appointment.title}</p>
                                            {from && until && (
                                                <p className='text-sm'>
                                                    {format(from, 'dd.MM.yyyy, HH:mm', { locale: de })} – {format(until, 'HH:mm', { locale: de })}
                                                </p>
                                            )}
                                            {practitioners.length > 0 && (
                                                <ul className='text-sm text-muted-foreground'>
                                                    {practitioners.map(p => (
                                                        <li key={p.id}>{p.title ? `${p.title} ` : ''}{p.firstName} {p.lastName}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    );
                                })()}
                                {billingDraft.createdBy && (
                                    <div>
                                        <span className='text-sm text-muted-foreground'>Erstellt von</span>
                                        <p className='font-medium'>{billingDraft.createdBy.firstName} {billingDraft.createdBy.lastName}</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <BillingDraftLogsModal
                    billingDraftId={billingDraft.id}
                    open={showLogsModal}
                    onClose={() => setShowLogsModal(false)}
                />

                {/* Add Payment Dialog */}
                {billingDraft && billingDraft.type !== 'INFLUENCER' && billingDraft.type !== 'MARKETING' && billingDraft.type !== 'KULANZ' && (
                    <AddPaymentDialog
                        storeId={billingDraft.storeId}
                        open={showAddPaymentDialog}
                        onOpenChange={setShowAddPaymentDialog}
                        billingDraftId={billingDraft.id}
                        remainingAmountEur={(() => {
                            const totalPaidCents = payments?.reduce((sum, p) => 
                                p.status === 'COMPLETED' ? sum + p.paidAmountCents : sum, 0
                            ) ?? 0;
                            const totalPaidEur = totalPaidCents / 100;
                            const billingDraftTotal = Number(billingDraft.totalGrossDiscounted);
                            return Math.max(0, billingDraftTotal - totalPaidEur);
                        })()}
                        onSuccess={() => {
                            // After payment, refresh invoices (auto-generated on first payment)
                            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id! });
                        }}
                    />
                )}

                {/* Edit Billing Draft Dialog */}
                {billingDraft && showEditDialog && (
                    <BillingDraftEditor
                        open={showEditDialog}
                        onOpenChange={setShowEditDialog}
                        billingDraft={billingDraft as any}
                        onSuccess={() => {
                            trpcUtils.billingDrafts.getOne.invalidate({ id: id! });
                            trpcUtils.invoices.getByBillingDraft.invalidate({ billingDraftId: id! });
                            trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId: id! });
                        }}
                    />
                )}

                {/* Refund Payment Dialog */}
                <RefundPaymentDialog
                    open={showRefundPaymentDialog}
                    onOpenChange={(open) => {
                        if (!open) setShowRefundPaymentDialog(false);
                    }}
                    billingDraftId={billingDraft.id}
                    storeId={billingDraft.storeId}
                    payments={payments}
                    totalGrossDiscounted={Number(billingDraft.totalGrossDiscounted)}
                    onSuccess={() => {
                        trpcUtils.billingDrafts.getOne.invalidate({ id: id! });
                        trpcUtils.payments.getByBillingDraft.invalidate({ billingDraftId: id! });
                    }}
                />

                {/* Cancel Line Item Dialog */}
                <AlertDialog
                    open={!!cancelLineItemTarget}
                    onOpenChange={(open) => {
                        if (!open) {
                            setCancelLineItemTarget(null);
                        }
                    }}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Position stornieren</AlertDialogTitle>
                            <AlertDialogDescription>
                                Diese Aktion kann nicht rückgängig gemacht werden. Es wird eine Storno-Rechnung für die Position &quot;{cancelLineItemTarget?.description}&quot; erstellt.
                            </AlertDialogDescription>
                        </AlertDialogHeader>

                        {cancelLineItemTarget?.bundleVoucherTransactions && cancelLineItemTarget.bundleVoucherTransactions.length > 0 && (
                            <div className='space-y-3 border rounded-lg p-3 bg-muted/50'>
                                <p className='text-sm font-medium'>Bundle-Gutschein Nutzung</p>
                                <div className='space-y-2'>
                                    {cancelLineItemTarget.bundleVoucherTransactions.map((tx: any) => (
                                        <div key={tx.id} className='text-sm flex items-center justify-between'>
                                            <span className='text-muted-foreground'>
                                                {tx.bundleVoucher?.treatmentVariant?.name || 'Bundle-Gutschein'}
                                            </span>
                                            <span className='font-mono'>
                                                {Math.abs(tx.quantityUnits)} {Math.abs(tx.quantityUnits) === 1 ? 'Einheit' : 'Einheiten'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className='flex items-center space-x-2 pt-1'>
                                    <Checkbox
                                        id='reverse-bundle-voucher'
                                        checked={reverseBundleVoucher}
                                        onCheckedChange={(checked) => setReverseBundleVoucher(checked === true)}
                                    />
                                    <Label htmlFor='reverse-bundle-voucher' className='text-sm cursor-pointer'>
                                        Bundle-Gutschein Nutzung rückgängig machen
                                    </Label>
                                </div>
                                {reverseBundleVoucher && (
                                    <p className='text-xs text-muted-foreground'>
                                        Die verbrauchten Einheiten werden dem Bundle-Gutschein wieder gutgeschrieben.
                                    </p>
                                )}
                            </div>
                        )}

                        {isLastActiveLineItem && (() => {
                            const giftCardPayments = (payments || []).filter((p: any) =>
                                p.paymentMethod === 'GIFT_CARD' && p.type === 'BILLING_DRAFT_PAYMENT' && p.paidAmountCents > 0
                            );
                            if (giftCardPayments.length === 0) {
                                return null;
                            }
                            const totalGiftCardCents = giftCardPayments.reduce((sum: number, p: any) => sum + p.paidAmountCents, 0);
                            return (
                                <div className='space-y-3 border rounded-lg p-3 bg-muted/50'>
                                    <p className='text-sm font-medium'>Gutscheinkarte Zahlung</p>
                                    <p className='text-sm text-muted-foreground'>
                                        {formatCurrency(totalGiftCardCents / 100)} wurde mit Gutscheinkarte(n) bezahlt.
                                    </p>
                                    <div className='flex items-center space-x-2 pt-1'>
                                        <Checkbox
                                            id='reverse-gift-card'
                                            checked={reverseGiftCard}
                                            onCheckedChange={(checked) => setReverseGiftCard(checked === true)}
                                        />
                                        <Label htmlFor='reverse-gift-card' className='text-sm cursor-pointer'>
                                            Gutscheinkarte Guthaben zurückbuchen
                                        </Label>
                                    </div>
                                    {reverseGiftCard && (
                                        <p className='text-xs text-muted-foreground'>
                                            Der bezahlte Betrag wird der Gutscheinkarte gutgeschrieben.
                                        </p>
                                    )}
                                </div>
                            );
                        })()}

                        <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => {
                                    if (cancelLineItemTarget) {
                                        cancelLineItemMutation.mutate({
                                            lineItemId: cancelLineItemTarget.id,
                                            reverseBundleVoucher,
                                            reverseGiftCard,
                                        });
                                        setCancelLineItemTarget(null);
                                    }
                                }}
                                className='bg-destructive text-white hover:bg-destructive/90'
                            >
                                Stornieren
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Bundle Cancel Warning Dialog (bundle has been used, can't cancel) */}
                <AlertDialog
                    open={!!bundleCancelWarningInfo}
                    onOpenChange={(open) => {
                        if (!open) {
                            setBundleCancelWarningInfo(null);
                        }
                    }}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Stornierung nicht möglich</AlertDialogTitle>
                            <AlertDialogDescription>
                                Das Bundle wurde bereits genutzt und kann daher nicht storniert werden.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        {bundleCancelWarningInfo && (
                            <div className='space-y-2'>
                                {bundleCancelWarningInfo.vouchers.map((v, idx) => (
                                    <div key={idx} className='border rounded-lg p-3 bg-muted/50'>
                                        <p className='text-sm font-medium'>{v.treatmentVariantName}</p>
                                        <p className='text-sm text-muted-foreground'>
                                            {v.usedUnits} von {v.unitQuantity} Einheiten verbraucht
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                        <AlertDialogFooter>
                            <AlertDialogAction onClick={() => setBundleCancelWarningInfo(null)}>
                                Verstanden
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Bundle Cancel Confirmation Dialog (bundle unused, will be deleted) */}
                <AlertDialog
                    open={!!bundleCancelConfirmInfo}
                    onOpenChange={(open) => {
                        if (!open) {
                            setBundleCancelConfirmInfo(null);
                        }
                    }}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Bundle stornieren und löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Durch die Stornierung wird das zugehörige Bundle unwiderruflich gelöscht. Alle verbleibenden Einheiten werden entfernt.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        {bundleCancelConfirmInfo && (
                            <div className='space-y-2'>
                                {bundleCancelConfirmInfo.vouchers.map((v, idx) => (
                                    <div key={idx} className='border rounded-lg p-3 bg-destructive/10'>
                                        <p className='text-sm font-medium'>{v.treatmentVariantName}</p>
                                        <p className='text-sm text-muted-foreground'>
                                            {v.remainingUnits} von {v.unitQuantity} Einheiten werden entfernt
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                        {isLastActiveLineItem && (() => {
                            const giftCardPayments = (payments || []).filter((p: any) =>
                                p.paymentMethod === 'GIFT_CARD' && p.type === 'BILLING_DRAFT_PAYMENT' && p.paidAmountCents > 0
                            );
                            if (giftCardPayments.length === 0) {
                                return null;
                            }
                            const totalGiftCardCents = giftCardPayments.reduce((sum: number, p: any) => sum + p.paidAmountCents, 0);
                            return (
                                <div className='space-y-3 border rounded-lg p-3 bg-muted/50'>
                                    <p className='text-sm font-medium'>Gutscheinkarte Zahlung</p>
                                    <p className='text-sm text-muted-foreground'>
                                        {formatCurrency(totalGiftCardCents / 100)} wurde mit Gutscheinkarte(n) bezahlt.
                                    </p>
                                    <div className='flex items-center space-x-2 pt-1'>
                                        <Checkbox
                                            id='reverse-gift-card-bundle'
                                            checked={reverseGiftCard}
                                            onCheckedChange={(checked) => setReverseGiftCard(checked === true)}
                                        />
                                        <Label htmlFor='reverse-gift-card-bundle' className='text-sm cursor-pointer'>
                                            Gutscheinkarte Guthaben zurückbuchen
                                        </Label>
                                    </div>
                                    {reverseGiftCard && (
                                        <p className='text-xs text-muted-foreground'>
                                            Der bezahlte Betrag wird der Gutscheinkarte gutgeschrieben.
                                        </p>
                                    )}
                                </div>
                            );
                        })()}
                        <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => {
                                    if (bundleCancelConfirmInfo) {
                                        cancelBundleWithArchiveMutation.mutate({
                                            lineItemId: bundleCancelConfirmInfo.lineItemId,
                                            reverseGiftCard,
                                        });
                                    }
                                }}
                                className='bg-destructive text-white hover:bg-destructive/90'
                            >
                                {cancelBundleWithArchiveMutation.isPending ? (
                                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                ) : null}
                                Stornieren und löschen
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Gift Card Cancel Warning Dialog (gift card has been used, can't cancel) */}
                <AlertDialog
                    open={!!giftCardCancelWarningInfo}
                    onOpenChange={(open) => {
                        if (!open) {
                            setGiftCardCancelWarningInfo(null);
                        }
                    }}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Stornierung nicht möglich</AlertDialogTitle>
                            <AlertDialogDescription>
                                Die Gutscheinkarte wurde bereits genutzt und kann daher nicht storniert werden.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        {giftCardCancelWarningInfo && (
                            <div className='space-y-2'>
                                {giftCardCancelWarningInfo.giftCards.map((gc, idx) => (
                                    <div key={idx} className='border rounded-lg p-3 bg-muted/50'>
                                        <p className='text-sm font-medium'>Gutscheinkarte {gc.giftCardNumber}</p>
                                        <p className='text-sm text-muted-foreground'>
                                            {formatCurrency(gc.usedAmount)} von {formatCurrency(gc.initialValueEur)} verbraucht
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                        <AlertDialogFooter>
                            <AlertDialogAction onClick={() => setGiftCardCancelWarningInfo(null)}>
                                Verstanden
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Gift Card Cancel Confirmation Dialog (gift card unused, will be deleted) */}
                <AlertDialog
                    open={!!giftCardCancelConfirmInfo}
                    onOpenChange={(open) => {
                        if (!open) {
                            setGiftCardCancelConfirmInfo(null);
                        }
                    }}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Gutscheinkarte stornieren und löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Durch die Stornierung wird die zugehörige Gutscheinkarte unwiderruflich gelöscht. Das gesamte Guthaben wird entfernt.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        {giftCardCancelConfirmInfo && (
                            <div className='space-y-2'>
                                {giftCardCancelConfirmInfo.giftCards.map((gc, idx) => (
                                    <div key={idx} className='border rounded-lg p-3 bg-destructive/10'>
                                        <p className='text-sm font-medium'>Gutscheinkarte {gc.giftCardNumber}</p>
                                        <p className='text-sm text-muted-foreground'>
                                            Guthaben {formatCurrency(gc.remainingBalance)} von {formatCurrency(gc.initialValueEur)} wird entfernt
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                        {isLastActiveLineItem && (() => {
                            const giftCardPayments = (payments || []).filter((p: any) =>
                                p.paymentMethod === 'GIFT_CARD' && p.type === 'BILLING_DRAFT_PAYMENT' && p.paidAmountCents > 0
                            );
                            if (giftCardPayments.length === 0) {
                                return null;
                            }
                            const totalGiftCardCents = giftCardPayments.reduce((sum: number, p: any) => sum + p.paidAmountCents, 0);
                            return (
                                <div className='space-y-3 border rounded-lg p-3 bg-muted/50'>
                                    <p className='text-sm font-medium'>Gutscheinkarte Zahlung</p>
                                    <p className='text-sm text-muted-foreground'>
                                        {formatCurrency(totalGiftCardCents / 100)} wurde mit Gutscheinkarte(n) bezahlt.
                                    </p>
                                    <div className='flex items-center space-x-2 pt-1'>
                                        <Checkbox
                                            id='reverse-gift-card-giftcard'
                                            checked={reverseGiftCard}
                                            onCheckedChange={(checked) => setReverseGiftCard(checked === true)}
                                        />
                                        <Label htmlFor='reverse-gift-card-giftcard' className='text-sm cursor-pointer'>
                                            Gutscheinkarte Guthaben zurückbuchen
                                        </Label>
                                    </div>
                                    {reverseGiftCard && (
                                        <p className='text-xs text-muted-foreground'>
                                            Der bezahlte Betrag wird der Gutscheinkarte gutgeschrieben.
                                        </p>
                                    )}
                                </div>
                            );
                        })()}
                        <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => {
                                    if (giftCardCancelConfirmInfo) {
                                        cancelGiftCardWithArchiveMutation.mutate({
                                            lineItemId: giftCardCancelConfirmInfo.lineItemId,
                                            reverseGiftCard,
                                        });
                                    }
                                }}
                                className='bg-destructive text-white hover:bg-destructive/90'
                            >
                                {cancelGiftCardWithArchiveMutation.isPending ? (
                                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                ) : null}
                                Stornieren und löschen
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* GOÄ Factor Below 1 Warning Dialog */}
                <AlertDialog
                    open={showGoaFactorWarning}
                    onOpenChange={(open) => {
                        if (!open) {
                            setShowGoaFactorWarning(false);
                        }
                    }}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Hinweis: GOÄ-Faktor unter 1</AlertDialogTitle>
                            <AlertDialogDescription>
                                Eine der erstellten Rechnungen enthält GOÄ-Positionen mit einem Faktor unter 1. Dies ist nicht zulässig. Bitte wenden Sie sich an einen Administrator.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogAction onClick={() => setShowGoaFactorWarning(false)}>
                                Verstanden
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </Content>
    );
};

// Placeholder component for the billing draft creator
const BillingDraftCreatorPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const customerId = searchParams.get('customerId');
    const appointmentId = searchParams.get('appointmentId');

    const navState = location.state as BillingDraftNavigationState | null;
    const handleBack = () => {
        if (navState?.from) {
            navigate(navState.from, { state: navState.tab ? { tab: navState.tab } : undefined });
        } else {
            navigate('/billing-drafts');
        }
    };

    return (
        <Content>
            <div className='container mx-auto py-10'>
                <div className='flex items-center gap-4 mb-6'>
                    <Button variant='ghost' size='icon' onClick={handleBack}>
                        <ArrowLeft className='h-4 w-4' />
                    </Button>
                    <h1 className='text-2xl font-bold'>Neuen Auftrag erstellen</h1>
                </div>
                
                {/* The BillingDraftCreator component is used here for billing draft creation */}
                <BillingDraftCreator
                    preselectedCustomerId={customerId ? parseInt(customerId) : undefined}
                    preselectedAppointmentId={appointmentId || undefined}
                    onSuccess={(billingDraftId) => navigate(`/billing-draft/${billingDraftId}`, { state: navState ?? undefined })}
                    onCancel={handleBack}
                />
            </div>
        </Content>
    );
};

export default BillingDraft;
